import { useState } from "react";
import { NavLink } from "react-router-dom";
import { ArrowDown, ArrowUp, ArrowDownAZ, ListOrdered, Check } from "lucide-react";
import { toast } from "sonner";
import { NavDot } from "./NavDots";
import { http, formatApiError } from "../lib/api";
import { tr } from "../i18n";

const Badge = ({ n, color, testid }) => (
  <span data-testid={testid} className={`ml-auto min-w-[20px] h-5 px-1.5 rounded-full text-white text-[10px] font-bold grid place-items-center ${color}`}>{n}</span>
);

const Item = ({ item, dots, counts, onNav }) => (
  <NavLink to={item.to} end={item.end} onClick={onNav} data-testid={`nav-${item.to.replace("/", "") || "home"}`}
    className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-semibold transition-colors duration-150 ${isActive ? "bg-sky-50 text-sky-700 shadow-[inset_2px_0_0_0_#0284c7]" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"}`}>
    <item.icon className="h-4 w-4 shrink-0" />
    <span className="min-w-0 truncate">{item.label}</span>
    {item.dot && dots[item.dot] && <NavDot k={item.dot} />}
    {item.badge && counts[item.badge] > 0 && <Badge n={counts[item.badge]} color={item.badge === "quotes" ? "bg-sky-600" : "bg-rose-600"} testid={`nav-${item.badge}-badge`} />}
  </NavLink>
);

const alpha = (items) => [...items].sort((a, b) => tr(a.label).localeCompare(tr(b.label), undefined, { sensitivity: "base" }));

export const orderNav = (items, order) => {
  if (!order?.length) return alpha(items);
  const pos = new Map(order.map((to, i) => [to, i]));
  const known = items.filter((n) => pos.has(n.to)).sort((a, b) => pos.get(a.to) - pos.get(b.to));
  return [...known, ...alpha(items.filter((n) => !pos.has(n.to)))];
};

// Menu à plat, une ligne par rubrique ; ordre alphabétique ou personnalisé par l'utilisateur.
export function SideNav({ items, order, onOrder, dots, counts, onNav }) {
  const [edit, setEdit] = useState(false);
  const list = orderNav(items, order);
  const save = (next) => {
    onOrder(next);
    http.put("/auth/me/nav-order", { order: next }).catch((e) => toast.error(formatApiError(e)));
  };
  const move = (i, d) => {
    const ids = list.map((n) => n.to); const j = i + d;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]]; save(ids);
  };
  return (
    <>
      {edit ? list.map((item, i) => (
        <div key={item.to} data-testid={`nav-order-row-${item.to.replace("/", "") || "home"}`} className="flex items-center gap-2 px-2 py-1.5 rounded-md ring-1 ring-slate-400/30 text-sm font-semibold">
          <item.icon className="h-4 w-4 shrink-0" /><span className="min-w-0 flex-1 truncate">{item.label}</span>
          <button type="button" aria-label="Monter" disabled={i === 0} onClick={() => move(i, -1)} data-testid={`nav-order-up-${i}`} className="p-1 rounded hover:bg-slate-400/20 disabled:opacity-30"><ArrowUp className="h-3.5 w-3.5" /></button>
          <button type="button" aria-label="Descendre" disabled={i === list.length - 1} onClick={() => move(i, 1)} data-testid={`nav-order-down-${i}`} className="p-1 rounded hover:bg-slate-400/20 disabled:opacity-30"><ArrowDown className="h-3.5 w-3.5" /></button>
        </div>
      )) : list.map((item) => <Item key={item.to} item={item} dots={dots} counts={counts} onNav={onNav} />)}
      <div className="flex items-center gap-3 px-3 pt-3 text-[11px] font-semibold text-slate-400">
        {edit ? (
          <>
            <button type="button" onClick={() => setEdit(false)} data-testid="nav-order-done" className="inline-flex items-center gap-1 text-sky-700 hover:underline"><Check className="h-3.5 w-3.5" /> Terminé</button>
            <button type="button" onClick={() => save([])} data-testid="nav-order-reset" className="inline-flex items-center gap-1 hover:text-slate-700 hover:underline"><ArrowDownAZ className="h-3.5 w-3.5" /> Ordre alphabétique</button>
          </>
        ) : (
          <button type="button" onClick={() => setEdit(true)} data-testid="nav-order-edit" className="inline-flex items-center gap-1 hover:text-slate-700 hover:underline"><ListOrdered className="h-3.5 w-3.5" /> Réorganiser le menu</button>
        )}
      </div>
    </>
  );
}
